import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, nowInTimezone, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { isToday, isThisWeek } from 'date-fns';
import { calculateConversionRate } from '@/utils/dateFieldStandards';
import { useTenant } from '@/hooks/useTenant';
import { useVisibility } from '@/hooks/useVisibility';

export interface PeriodComparison {
  percent: number;
  direction: 'up' | 'down' | 'neutral';
  label: string;
  isPositiveGood?: boolean;
}

interface PeriodComparisons {
  totalLeadsToday: PeriodComparison;
  phoneCalls: PeriodComparison;
  formSubmissions: PeriodComparison;
  manualEntries: PeriodComparison;
  totalLeadsOpen: PeriodComparison;
  leadsNeedFollowUp: PeriodComparison;
  leadsEstimateSent: PeriodComparison;
  leadsConverted: PeriodComparison;
  leadsLost: PeriodComparison;
  conversionRate: PeriodComparison;
}

interface LeadsDashboardMetrics {
  totalLeadsToday: number;
  totalLeadsOpen: number;
  leadsNeedFollowUp: number;
  leadsConverted: number;
  leadsLost: number;
  leadsEstimateSent: number;
  conversionRate: number;
  // Lead source breakdown
  phoneCalls: number;
  formSubmissions: number;
  manualEntries: number;
  // Follow-up metrics
  followUpsOverdue: number;
  followUpsDueToday: number;
  followUpsThisWeek: number;
  topCsr: {
    id: string;
    name: string;
    leadCount: number;
  } | null;
  locationBreakdown: Array<{
    location: string;
    count: number;
  }>;
  comparisons?: PeriodComparisons;
}

/** Calculate the mirror-image previous period for a given date range */
function calculatePreviousPeriod(dateFrom: Date, dateTo: Date) {
  const durationMs = dateTo.getTime() - dateFrom.getTime();
  const prevDateTo = new Date(dateFrom.getTime() - 1);
  const prevDateFrom = new Date(prevDateTo.getTime() - durationMs);
  return { prevDateFrom, prevDateTo };
}

/** Build a comparison object from current vs previous values */
function makeComparison(
  current: number,
  previous: number,
  label: string,
  isPositiveGood = true,
): PeriodComparison {
  if (current === previous) {
    return { percent: 0, direction: 'neutral', label, isPositiveGood };
  }
  if (previous === 0) {
    return { percent: 100, direction: current > 0 ? 'up' : 'neutral', label, isPositiveGood };
  }
  const pct = ((current - previous) / previous) * 100;
  return {
    percent: Math.abs(pct),
    direction: pct > 0 ? 'up' : 'down',
    label,
    isPositiveGood,
  };
}

type LeadRow = {
  id: string;
  status: string;
  created_at: string;
  customer_address: string | null;
  assigned_csr_id: string | null;
  next_follow_up_date: string | null;
  lead_entry_type: string | null;
};

/** Compute aggregate metrics from a set of leads */
function computePeriodMetrics(leads: LeadRow[]) {
  const total = leads.length;
  const totalLeadsOpen = leads.filter(l => ['new', 'processing', 'needs_follow_up', 'estimate_sent', 'estimate_accepted'].includes(l.status)).length;
  const leadsNeedFollowUp = leads.filter(l => l.status === 'needs_follow_up').length;
  const leadsConverted = leads.filter(l => l.status === 'converted').length;
  const leadsLost = leads.filter(l => l.status === 'lost').length;
  const leadsEstimateSent = leads.filter(l => l.status === 'estimate_sent').length;
  const phoneCalls = leads.filter(l => l.lead_entry_type === 'webhook').length;
  const formSubmissions = leads.filter(l => l.lead_entry_type === 'form').length;
  const manualEntries = leads.filter(l => l.lead_entry_type === 'manual').length;
  const conversionRate = calculateConversionRate(leadsConverted, total);
  return { total, totalLeadsOpen, leadsNeedFollowUp, leadsConverted, leadsLost, leadsEstimateSent, phoneCalls, formSubmissions, manualEntries, conversionRate };
}

// Helper function to extract location from address
function parseLocationFromAddress(address: string | null): string {
  if (!address) return 'Unknown';

  const addressLower = address.toLowerCase();

  // Common Australian cities/suburbs
  const knownCities = [
    'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide',
    'gold coast', 'newcastle', 'canberra', 'sunshine coast',
    'wollongong', 'geelong', 'hobart', 'townsville', 'cairns'
  ];

  // Check if any known city is in the address
  for (const city of knownCities) {
    if (addressLower.includes(city)) {
      return city.split(' ').map(w =>
        w.charAt(0).toUpperCase() + w.slice(1)
      ).join(' ');
    }
  }

  // Try to extract from comma-separated format
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const possibleLocation = parts[1];
    // Remove postcodes and state codes
    const cleaned = possibleLocation
      .replace(/\b\d{4}\b/g, '') // Remove 4-digit postcodes
      .replace(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\b/gi, '') // Remove state codes
      .trim();

    if (cleaned.length > 2) {
      return cleaned.split(' ').map(w =>
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      ).join(' ');
    }
  }

  return 'Other';
}

export function useLeadsDashboard(
  dateFrom?: Date,
  dateTo?: Date,
  compareEnabled = false,
  timezone: string = DEFAULT_TIMEZONE,
) {
  const canCompare = compareEnabled && !!dateFrom && !!dateTo;
  const prevDates = canCompare ? calculatePreviousPeriod(dateFrom, dateTo) : null;
  const { userId } = useTenant();
  const { isRestricted } = useVisibility();
  // Non-admins only ever see the pipeline for leads assigned to them.
  const csrId = isRestricted ? userId : undefined;

  return useQuery<LeadsDashboardMetrics>({
    queryKey: ['leads-dashboard', dateFrom?.toISOString(), dateTo?.toISOString(), compareEnabled, timezone, csrId ?? 'all'],
    enabled: !isRestricted || !!csrId,
    queryFn: async () => {
      // Fetch leads — widen the date range to include previous period in the SAME query
      let query = supabase
        .from('leads')
        .select(`
          id,
          status,
          created_at,
          customer_address,
          assigned_csr_id,
          next_follow_up_date,
          lead_entry_type
        `);

      if (csrId) {
        query = query.eq('assigned_csr_id', csrId);
      }

      const effectiveDateFrom = prevDates ? prevDates.prevDateFrom : dateFrom;
      if (effectiveDateFrom) {
        query = query.gte('created_at', effectiveDateFrom.toISOString());
      }
      if (dateTo) {
        query = query.lte('created_at', dateTo.toISOString());
      }

      const { data: allFetchedLeads, error } = await query;
      if (error) throw error;

      // Partition into current and previous periods (in-memory split, zero extra queries)
      const currentLeads = (allFetchedLeads || []).filter(l => {
        const d = new Date(l.created_at);
        return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
      }) as LeadRow[];

      const previousLeads = prevDates
        ? ((allFetchedLeads || []).filter(l => {
            const d = new Date(l.created_at);
            return d >= prevDates.prevDateFrom && d <= prevDates.prevDateTo;
          }) as LeadRow[])
        : [];

      // Use currentLeads for all metric calculations
      const leads = currentLeads;

      // Fetch CSR profiles separately
      const csrIds = [...new Set(leads.map(l => l.assigned_csr_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', csrIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Calculate today's leads (always shows today in configured timezone regardless of filter)
      const todayStart = startOfDayInTz(nowInTimezone(timezone), timezone);
      const todayEnd = endOfDayInTz(nowInTimezone(timezone), timezone);
      const totalLeadsToday = leads.filter(
        l => new Date(l.created_at) >= todayStart &&
          new Date(l.created_at) <= todayEnd
      ).length;

      // Compute core metrics for current period
      const currentMetrics = computePeriodMetrics(leads);

      // Calculate follow-up metrics (only for open leads in current period)
      const openLeads = leads.filter(l => ['new', 'processing', 'needs_follow_up', 'estimate_sent', 'estimate_accepted'].includes(l.status));
      const todayDateOnly = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate());

      const followUpsOverdue = openLeads.filter(l => {
        if (!l.next_follow_up_date) return false;
        const followUpDay = new Date(l.next_follow_up_date);
        const dayOnly = new Date(followUpDay.getFullYear(), followUpDay.getMonth(), followUpDay.getDate());
        return dayOnly < todayDateOnly;
      }).length;

      const followUpsDueToday = openLeads.filter(l => {
        if (!l.next_follow_up_date) return false;
        return isToday(new Date(l.next_follow_up_date));
      }).length;

      const followUpsThisWeek = openLeads.filter(l => {
        if (!l.next_follow_up_date) return false;
        const date = new Date(l.next_follow_up_date);
        return isThisWeek(date) && !isToday(date) && date >= todayDateOnly;
      }).length;

      // Find top CSR
      const csrCounts = new Map<string, { name: string; count: number }>();
      leads.forEach(lead => {
        if (lead.assigned_csr_id) {
          const csrId = lead.assigned_csr_id;
          const profile = profileMap.get(csrId);
          const csrName = profile?.full_name || 'Unknown';
          const current = csrCounts.get(csrId) || { name: csrName, count: 0 };
          csrCounts.set(csrId, { name: csrName, count: current.count + 1 });
        }
      });

      let topCsr = null;
      let maxCount = 0;
      csrCounts.forEach((value, key) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          topCsr = { id: key, name: value.name, leadCount: value.count };
        }
      });

      // Parse locations from addresses
      const locationCounts = new Map<string, number>();
      leads.forEach(lead => {
        const loc = parseLocationFromAddress(lead.customer_address);
        const count = locationCounts.get(loc) || 0;
        locationCounts.set(loc, count + 1);
      });

      const locationBreakdown = Array.from(locationCounts.entries())
        .map(([loc, count]) => ({ location: loc, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Build period-over-period comparisons when compare is enabled
      let comparisons: PeriodComparisons | undefined;
      if (canCompare && previousLeads.length >= 0) {
        const prevMetrics = computePeriodMetrics(previousLeads);
        const label = 'vs prev. period';
        comparisons = {
          totalLeadsToday: makeComparison(currentMetrics.total, prevMetrics.total, label),
          phoneCalls:      makeComparison(currentMetrics.phoneCalls, prevMetrics.phoneCalls, label),
          formSubmissions: makeComparison(currentMetrics.formSubmissions, prevMetrics.formSubmissions, label),
          manualEntries:   makeComparison(currentMetrics.manualEntries, prevMetrics.manualEntries, label),
          totalLeadsOpen:  makeComparison(currentMetrics.totalLeadsOpen, prevMetrics.totalLeadsOpen, label),
          leadsNeedFollowUp: makeComparison(currentMetrics.leadsNeedFollowUp, prevMetrics.leadsNeedFollowUp, label),
          leadsEstimateSent: makeComparison(currentMetrics.leadsEstimateSent, prevMetrics.leadsEstimateSent, label),
          leadsConverted:  makeComparison(currentMetrics.leadsConverted, prevMetrics.leadsConverted, label),
          leadsLost:       makeComparison(currentMetrics.leadsLost, prevMetrics.leadsLost, label, false),
          conversionRate:  makeComparison(currentMetrics.conversionRate, prevMetrics.conversionRate, label),
        };
      }

      return {
        totalLeadsToday,
        totalLeadsOpen: currentMetrics.totalLeadsOpen,
        leadsNeedFollowUp: currentMetrics.leadsNeedFollowUp,
        leadsConverted: currentMetrics.leadsConverted,
        leadsLost: currentMetrics.leadsLost,
        leadsEstimateSent: currentMetrics.leadsEstimateSent,
        conversionRate: currentMetrics.conversionRate,
        phoneCalls: currentMetrics.phoneCalls,
        formSubmissions: currentMetrics.formSubmissions,
        manualEntries: currentMetrics.manualEntries,
        followUpsOverdue,
        followUpsDueToday,
        followUpsThisWeek,
        topCsr,
        locationBreakdown,
        comparisons,
      };
    },
  });
}
